import {
  DEFAULT_PRICING_PLANS,
  DEFAULT_TIER_RULES,
  LEGACY_AUCTION_FEE_PCT,
  PRICING_PLAN_TIERS,
  type PensionFundTier,
  type PricingPlan,
  type PricingPlans,
  type PricingPlanTier,
  type PricingRuleSet,
  type TierRules,
} from '@carsfinders/contracts';

/**
 * The tariffs page's EDITS, as pure functions (Phase 7 review, C3b).
 *
 * The page could previously edit `marginalBands` and nothing else, so it
 * re-published `swissExport`, `fx` and `customs.pensionFundTiers` verbatim from
 * whatever it loaded — which made the documented `ua-2026-01 -> ua-2026-02`
 * migration impossible through the only screen that exists for it. The page can
 * now edit all of them, and because every edit lives here rather than inside a
 * `useCallback`, that claim is testable without a DOM: `pricing-edits.test.ts`
 * drives a REAL legacy-shaped document to `ua-2026-02` through these functions
 * alone and then validates the result with the same check the API refuses with.
 */

/** Replace one marginal band's bound. */
export function setBandUpTo(
  rules: PricingRuleSet,
  tier: PricingPlanTier,
  index: number,
  upTo: number | null,
): PricingRuleSet {
  const bands = rules.plans[tier].marginalBands;
  if (!bands) return rules;
  return {
    ...rules,
    plans: {
      ...rules.plans,
      [tier]: {
        ...rules.plans[tier],
        marginalBands: bands.map((band, i) => (i === index ? { ...band, upTo } : band)),
      },
    },
  };
}

/** Replace one marginal band's percentage. */
export function setBandPct(
  rules: PricingRuleSet,
  tier: PricingPlanTier,
  index: number,
  pct: number,
): PricingRuleSet {
  const bands = rules.plans[tier].marginalBands;
  if (!bands) return rules;
  return {
    ...rules,
    plans: {
      ...rules.plans,
      [tier]: {
        ...rules.plans[tier],
        marginalBands: bands.map((band, i) => (i === index ? { ...band, pct } : band)),
      },
    },
  };
}

/** Patch the Swiss leg: platform fee, its floor, the flat fee, the FX rate. */
export function patchSwissExport(
  rules: PricingRuleSet,
  patch: Partial<PricingRuleSet['swissExport']>,
): PricingRuleSet {
  return { ...rules, swissExport: { ...rules.swissExport, ...patch } };
}

/** Patch the FX block — `uahPerEur` is what the pension tiers are read against. */
export function patchFx(
  rules: PricingRuleSet,
  patch: Partial<PricingRuleSet['fx']>,
): PricingRuleSet {
  return { ...rules, fx: { ...rules.fx, ...patch } };
}

/** Patch one pension-fund tier (UAH threshold and/or rate). */
export function setPensionTier(
  rules: PricingRuleSet,
  index: number,
  patch: Partial<PensionFundTier>,
): PricingRuleSet {
  return {
    ...rules,
    customs: {
      ...rules.customs,
      pensionFundTiers: rules.customs.pensionFundTiers.map((tier, i) =>
        i === index ? { ...tier, ...patch } : tier,
      ),
    },
  };
}

/**
 * Replace a tier with the seed plan (percentage with a minimum, no bands).
 *
 * A stored pre-Phase-7 document falls back to the LEGACY linear grid, which the
 * publish invariant refuses (flat part, monthly fee) — so without this the owner
 * cannot compose a compliant grid at all. The seed plan is a STARTING POINT, not
 * a published value: nothing reaches the database until Publish.
 */
export function adoptSeedBands(rules: PricingRuleSet, tier: PricingPlanTier): PricingRuleSet {
  return {
    ...rules,
    plans: {
      ...rules.plans,
      // The WHOLE seed tier and nothing of the old one: the seed carries no
      // `marginalBands`, and a spread would keep a stale ladder that overrides
      // the seed's percentage and minimum.
      [tier]: { ...DEFAULT_PRICING_PLANS[tier] },
    },
  };
}

// ── The percentage-with-minimum grid (volume-tier commission, SC-11) ─────────

/** Patch one tier's plan, leaving every other tier and field untouched. */
function patchPlan(
  rules: PricingRuleSet,
  tier: PricingPlanTier,
  patch: Partial<PricingPlan>,
): PricingRuleSet {
  return { ...rules, plans: { ...rules.plans, [tier]: { ...rules.plans[tier], ...patch } } };
}

/** A tier's commission rate, as a FRACTION (the page shows and edits it as a percentage). */
export function setPlanSuccessFeePct(
  rules: PricingRuleSet,
  tier: PricingPlanTier,
  successFeePct: number,
): PricingRuleSet {
  return patchPlan(rules, tier, { successFeePct });
}

/** A tier's minimum commission, EUR. */
export function setPlanMinFee(
  rules: PricingRuleSet,
  tier: PricingPlanTier,
  minFee: number,
): PricingRuleSet {
  return patchPlan(rules, tier, { minFee });
}

/** The result of {@link clearLegacyFees}: the document, and which tiers it changed. */
export interface LegacyFeesCleared {
  rules: PricingRuleSet;
  /** Tiers whose `successFeeFlat` or `monthlyFee` was non-zero and is now 0. */
  clearedTiers: PricingPlanTier[];
}

/**
 * Compose a new version from a LEGACY document: `successFeeFlat` and `monthlyFee`
 * are no longer part of the tariff (the publish invariant refuses both), and the
 * page no longer offers an input for either — so they are zeroed here, once, and
 * the page says it did so. Leaving them would make the grid unpublishable with
 * no field on screen the owner could change to fix it.
 */
export function clearLegacyFees(rules: PricingRuleSet): LegacyFeesCleared {
  const clearedTiers = PRICING_PLAN_TIERS.filter(
    (tier) => rules.plans[tier].successFeeFlat !== 0 || rules.plans[tier].monthlyFee !== 0,
  );
  if (clearedTiers.length === 0) return { rules, clearedTiers };
  const plans = PRICING_PLAN_TIERS.reduce<PricingPlans>(
    (acc, tier) =>
      clearedTiers.includes(tier)
        ? { ...acc, [tier]: { ...acc[tier], successFeeFlat: 0, monthlyFee: 0 } }
        : acc,
    rules.plans,
  );
  return { rules: { ...rules, plans }, clearedTiers };
}

/**
 * Volume tiers on or off. Off is `null` — today's behaviour and the rollback.
 * On keeps the document's own rules, or pre-fills {@link DEFAULT_TIER_RULES}
 * when it had none.
 */
export function setVolumeTiersEnabled(rules: PricingRuleSet, enabled: boolean): PricingRuleSet {
  if (!enabled) return { ...rules, tierRules: null };
  return { ...rules, tierRules: rules.tierRules ?? cloneTierRules(DEFAULT_TIER_RULES) };
}

/** One editable field of the volume-tier rules, flattened for the form. */
export interface TierRulesPatch {
  windowDays?: number;
  holdDays?: number;
  dealer?: number;
  dealer_pro?: number;
}

/** Patch the volume-tier rules; a no-op while they are switched off. */
export function patchTierRules(rules: PricingRuleSet, patch: TierRulesPatch): PricingRuleSet {
  const current = rules.tierRules;
  if (!current) return rules;
  const { dealer, dealer_pro: dealerPro, ...windows } = patch;
  return {
    ...rules,
    tierRules: {
      ...current,
      ...windows,
      thresholds: {
        dealer: dealer ?? current.thresholds.dealer,
        dealer_pro: dealerPro ?? current.thresholds.dealer_pro,
      },
    },
  };
}

/**
 * «Use recommended grid»: all three plans from {@link DEFAULT_PRICING_PLANS} and
 * the volume tiers from {@link DEFAULT_TIER_RULES}. Fills the form only — nothing
 * reaches the database until Publish.
 */
export function applyRecommendedGrid(rules: PricingRuleSet): PricingRuleSet {
  const plans = PRICING_PLAN_TIERS.reduce<PricingPlans>(
    (acc, tier) => ({ ...acc, [tier]: { ...DEFAULT_PRICING_PLANS[tier] } }),
    DEFAULT_PRICING_PLANS,
  );
  return { ...rules, plans, tierRules: cloneTierRules(DEFAULT_TIER_RULES) };
}

function cloneTierRules(rules: TierRules): TierRules {
  return { ...rules, thresholds: { ...rules.thresholds } };
}

// ── Publishing: WHEN the grid takes effect ───────────────────────────────────

/**
 * What the owner is about to publish, as the two publish fields hold it.
 *
 * `validFromLocal` is the raw `<input type="datetime-local">` value
 * ("2026-09-18T14:05"), kept as a string rather than a Date so the blocker
 * below can tell "not filled in / not a date" from "a date that is too early" —
 * the page has to say something different about each.
 */
export interface PublishIntent {
  version: string;
  validFromLocal: string;
}

/** Why this publish cannot go out yet. */
export interface PublishBlocker {
  code: 'VERSION_UNCHANGED' | 'VALID_FROM_INVALID' | 'VALID_FROM_NOT_AFTER_INCUMBENT';
  /** The incumbent's effective date — what the operator has to get past. */
  incumbentValidFrom: Date;
}

/** `<input type="datetime-local">` wants local wall-clock, minute precision. */
export function toDateTimeLocal(date: Date): string {
  const pad = (value: number): string => String(value).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/** The instant a `datetime-local` value names, or null when it names none. */
export function parseDateTimeLocal(value: string): Date | null {
  if (value.trim() === '') return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Why a publish would not TAKE EFFECT — the check the page was missing
 * (Phase 7 re-review, N1).
 *
 * The page bumped `customs.version` and nothing else, so a published grid
 * carried the incumbent's `customs.validFrom` (2026-01-01 for both the stored
 * `ua-2026-01` document and the `ua-2026-02` seed). `findActiveRuleSet` picks
 * the newest `validFrom` and MongoDB guarantees no order among equal keys, so
 * the owner signed the tariff, published it, saw "published" — and the API went
 * on billing the old grid, with no error on any surface. The API-side tiebreaker
 * makes that case DEFINED; this makes it VISIBLE, which is the half the operator
 * can act on.
 *
 * Pure, and therefore testable without a DOM — the same reason every other edit
 * on this page lives in this file.
 */
export function findPublishBlocker(
  incumbent: PricingRuleSet,
  intent: PublishIntent,
): PublishBlocker | null {
  const incumbentValidFrom = incumbent.customs.validFrom;

  if (intent.version.trim() === '' || intent.version.trim() === incumbent.customs.version) {
    // History is immutable, so publishing under the loaded version is a 409
    // every time. Say so before the round trip.
    return { code: 'VERSION_UNCHANGED', incumbentValidFrom };
  }

  const validFrom = parseDateTimeLocal(intent.validFromLocal);
  if (!validFrom) return { code: 'VALID_FROM_INVALID', incumbentValidFrom };
  if (validFrom.getTime() <= incumbentValidFrom.getTime()) {
    return { code: 'VALID_FROM_NOT_AFTER_INCUMBENT', incumbentValidFrom };
  }

  return null;
}

/**
 * The document the page PUTs: the edited rule set, under a new version and a
 * new effective date.
 *
 * Both fields, together. A version bump alone renames a grid that never becomes
 * active; an effective date alone is a 409 on the immutable version.
 */
export function buildPublishPayload(
  rules: PricingRuleSet,
  version: string,
  validFrom: Date,
): PricingRuleSet {
  return { ...rules, customs: { ...rules.customs, version: version.trim(), validFrom } };
}

// ── The platform fee's parameterization, chosen rather than inferred ─────────

/** Which of the two platform-fee parameterizations a rule set is using. */
export type PlatformFeeModel = 'percentage' | 'flat';

/** Read the model off a rule set — `auctionFeePct: null` IS the flat form. */
export function platformFeeModel(rules: PricingRuleSet): PlatformFeeModel {
  return rules.swissExport.auctionFeePct === null ? 'flat' : 'percentage';
}

/**
 * Switch the platform fee between its percentage and flat forms EXPLICITLY
 * (Phase 7 re-review, N4).
 *
 * The page used to infer it: clearing the `auctionFeePct` box wrote `null`, and
 * `resolveAuctionFeeChf` then silently fell through to the flat `exportFeeChf` —
 * a different commercial model, reached by deleting three characters, with
 * nothing on screen saying the model had changed. Switching to `percentage`
 * restores the fee already in the document, or the pre-Phase-7 2.5% when there
 * is none, so the choice never lands on a rate of zero by accident.
 */
export function setPlatformFeeModel(
  rules: PricingRuleSet,
  model: PlatformFeeModel,
): PricingRuleSet {
  if (model === 'flat') return patchSwissExport(rules, { auctionFeePct: null });
  return patchSwissExport(rules, {
    auctionFeePct: rules.swissExport.auctionFeePct ?? LEGACY_AUCTION_FEE_PCT,
  });
}
